// Small TSL pattern helpers shared by the overlay layers. All are periodic across WORLD_W (stripe
// counts are whole numbers per world width), so the east-west wrap never shows a seam.

import { float, mix, smoothstep } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { WORLD_W } from '../../core/grid.ts';
import { periodFor } from '../tsl/noise.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;
type V3 = Node<'vec3'>;

const SQRT2 = Math.SQRT2;
const MIN_AA = 1e-4;
/** Stripe blur (in stripe periods) above which a stripe pattern averages to its duty cycle. */
const AVERAGE_FROM = 0.22;
const AVERAGE_TO = 0.5;
/** Rec. 709 luma weights (linear). */
const LUMA = [0.2126, 0.7152, 0.0722] as const;

/** Luminance of a linear colour. */
export function luma(c: V3): F {
  return c.x.mul(LUMA[0]).add(c.y.mul(LUMA[1])).add(c.z.mul(LUMA[2]));
}

/**
 * Anti-aliased diagonal stripes, 1 on a stripe and 0 between; `freq` is stripes per world unit
 * (rounded so the world holds a whole number). `duty` is the share of a period that is stripe.
 * Beyond ~4 pixels per period it fades to the average, so far views never shimmer.
 */
export function stripes(xz: V2, freq: number, duty: number, px: F): F {
  const perUnit = periodFor(freq) / WORLD_W;
  const s = xz.x.add(xz.y).mul(perUnit);
  const aa = px.mul(perUnit * SQRT2).max(MIN_AA);
  const d = s.fract().sub(0.5).abs();
  const half = duty / 2;
  const line = smoothstep(aa.add(half), aa.negate().add(half), d);
  return mix(line, float(duty), smoothstep(AVERAGE_FROM, AVERAGE_TO, aa));
}
